import { useState, useEffect } from "react";
import { Button } from "../components/ui/button";
import { Building2, Activity, LogOut, Menu, BarChart3, ClipboardList } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import OrganizationsManagement from "../components/OrganizationsManagement";
import SystemHealthDashboard from "../components/SystemHealthDashboard";
import SuperAdminDashboard from "../components/SuperAdminDashboard";
import ActivityLog from "../components/ActivityLog";
import { BACKEND_URL } from "../config/api";

const SuperAdminDashboardV2 = () => {
  const navigate = useNavigate();
  const [activeView, setActiveView] = useState(() => {
    return localStorage.getItem("superAdminActiveTab") || "dashboard";
  });
  // Default to collapsed (icon-only) sidebar
  const [sidebarExpanded, setSidebarExpanded] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);

  useEffect(() => {
    localStorage.setItem("superAdminActiveTab", activeView);
  }, [activeView]);

  useEffect(() => {
    const fetchPendingCount = async () => {
      try {
        const token = localStorage.getItem("jwtToken");
        // Use PostgreSQL v2 API
        const response = await axios.get(`${BACKEND_URL}/api/v2/super-admin/organizations`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        // v2 API returns { organizations: [...] }
        const orgs = response.data.organizations || response.data || [];
        const pending = orgs.filter(org => org.status === 'pending').length;
        setPendingCount(pending);
      } catch (error) {
        console.error("Error fetching pending count:", error);
      }
    };

    fetchPendingCount();
    const interval = setInterval(fetchPendingCount, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("jwtToken");
    localStorage.removeItem("userType");
    localStorage.removeItem("userData");
    toast.success("Logged out successfully");
    navigate("/login");
  };

  const menuItems = [
    { id: "dashboard", label: "Dashboard", icon: BarChart3 },
    { id: "accounts", label: "Accounts", icon: Building2, badge: pendingCount },
    { id: "activity", label: "Activity", icon: ClipboardList },
    { id: "health", label: "Health", icon: Activity },
  ];

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Mobile Backdrop */}
      {sidebarExpanded && (
        <div 
          className="fixed inset-0 bg-black/30 z-40 lg:hidden"
          onClick={() => setSidebarExpanded(false)}
        />
      )}

      {/* Sidebar - White bg with shadow */}
      <aside
        className={`${
          sidebarExpanded ? 'w-56' : 'w-20'
        } fixed lg:relative transition-all duration-200 bg-white flex flex-col h-full z-50 shadow-[4px_0_12px_rgba(0,0,0,0.03)]`}
      >
        {/* Logo & Toggle */}
        <div className="p-4 flex items-center justify-center">
          <button
            onClick={() => setSidebarExpanded(!sidebarExpanded)}
            className="p-2 rounded-xl hover:bg-gray-50 transition-colors"
            aria-label={sidebarExpanded ? "Collapse sidebar" : "Expand sidebar"}
          >
            {sidebarExpanded ? (
              <div className="flex items-center gap-3">
                <img 
                  src="/logo-icon.png" 
                  alt="BF" 
                  className="w-10 h-10 rounded-lg object-contain"
                />
                <span className="text-sm font-bold text-gray-900">
                  Booking<span className="text-blue-600">Flows</span>
                </span>
              </div>
            ) : (
              <img 
                src="/logo-icon.png" 
                alt="BF" 
                className="w-10 h-10 rounded-lg object-contain"
              />
            )}
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-2 space-y-2">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = activeView === item.id;
            
            return (
              <button
                key={item.id}
                onClick={() => {
                  setActiveView(item.id);
                  if (window.innerWidth < 1024) setSidebarExpanded(false);
                }}
                title={!sidebarExpanded ? item.label : undefined}
                className={`relative w-full flex items-center gap-3 p-3 rounded-xl transition-all ${
                  sidebarExpanded ? '' : 'justify-center'
                } ${
                  isActive 
                    ? 'bg-blue-600 text-white shadow-sm' 
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <Icon className="w-5 h-5 flex-shrink-0" />
                {sidebarExpanded && (
                  <span className="text-sm font-medium truncate">{item.label}</span>
                )}
                {/* Badge for pending count */}
                {item.badge > 0 && (
                  <span className={`absolute ${sidebarExpanded ? 'right-2' : 'top-0.5 right-0.5'} min-w-[18px] h-[18px] flex items-center justify-center rounded-full text-[10px] font-bold ${
                    isActive ? 'bg-white text-blue-600' : 'bg-blue-600 text-white'
                  }`}>
                    {item.badge}
                  </span>
                )}
              </button>
            );
          })}
        </nav>

        {/* Logout */}
        <div className="p-3">
          <button
            onClick={handleLogout}
            title={!sidebarExpanded ? "Logout" : undefined}
            className={`w-full flex items-center gap-3 p-3 rounded-xl text-red-600 hover:bg-red-50 transition-colors ${
              sidebarExpanded ? '' : 'justify-center'
            }`}
          >
            <LogOut className="w-5 h-5" />
            {sidebarExpanded && <span className="text-sm font-medium">Logout</span>}
          </button>
        </div>
      </aside>

      {/* Mobile Menu Toggle - Fixed when sidebar collapsed */}
      {!sidebarExpanded && (
        <button
          onClick={() => setSidebarExpanded(true)}
          className="fixed top-4 left-4 z-50 p-2.5 bg-white rounded-xl shadow-sm border border-gray-100 lg:hidden"
          aria-label="Open sidebar"
        >
          <Menu className="w-5 h-5 text-gray-600" />
        </button>
      )}

      {/* Main Content Area with max-width */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <main className="flex-1 overflow-auto">
          <div className="max-w-7xl mx-auto p-4 sm:p-6">
            {activeView === "dashboard" && <SuperAdminDashboard />}
            {activeView === "accounts" && <OrganizationsManagement />}
            {activeView === "activity" && <ActivityLog />}
            {activeView === "health" && <SystemHealthDashboard />}
          </div>
        </main>
      </div>
    </div>
  );
};

export default SuperAdminDashboardV2;
